import { useState } from 'react';
import { PlateScanButton } from './PlateScanner';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

/**
 * Atajos que van debajo del buscador de clientes: "Consumidor final" y
 * "Cliente sin datos (solo patente)".
 *
 * La resolucion de la patente (buscar el vehiculo o crear cliente "Sin datos")
 * vive aca; cada pantalla decide como enganchar el resultado a su formulario
 * via onPlateResolved. Si el auto no tiene patente, se cargan marca y modelo
 * a mano y el vehiculo se crea sin patente.
 */

export interface PlateResolution {
  /** true si la patente ya existia en el sistema */
  existed: boolean;
  /** { id, name, phone, placeholder } */
  client: any;
  /** vehiculo resuelto o creado (puede venir vacio) */
  vehicle: any;
  /** patente normalizada (mayusculas, sin separadores); '' si el auto no tiene */
  plate: string;
}

interface Props {
  /** Se llama al elegir "Consumidor final" */
  onConsumidorFinal: () => void;
  /** Se llama con el cliente/vehiculo resuelto a partir de la patente */
  onPlateResolved: (r: PlateResolution) => void;
  className?: string;
}

export default function ClientQuickPicks({ onConsumidorFinal, onPlateResolved, className }: Props) {
  const [open, setOpen] = useState(false);
  const [plateInput, setPlateInput] = useState('');
  const [sinPatente, setSinPatente] = useState(false);
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [saving, setSaving] = useState(false);

  const abrir = () => {
    setPlateInput('');
    setSinPatente(false);
    setMarca('');
    setModelo('');
    setOpen(true);
  };

  const resolver = async () => {
    const plate = plateInput.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (sinPatente) {
      if (!marca.trim() && !modelo.trim()) {
        toast.error('Cargá al menos la marca o el modelo del auto');
        return;
      }
    } else if (!plate) {
      return;
    }
    setSaving(true);
    try {
      const body = sinPatente ? { make: marca.trim(), model: modelo.trim() } : { plate };
      const res = await axios.post('/api/vehicles/resolve-plate', body);
      const { existed, vehicle, client, movidoDeConsumidorFinal } = res.data || {};
      if (!client?.id) return;
      setOpen(false);
      onPlateResolved({ existed: !!existed, client, vehicle, plate: sinPatente ? '' : plate });
      const etiqueta = sinPatente
        ? `${[marca.trim(), modelo.trim()].filter(Boolean).join(' ')} (sin patente)`
        : plate;
      toast.success(movidoDeConsumidorFinal
        ? `${vehicle?.licensePlate || plate} estaba en Consumidor Final → pasó a cliente "Sin datos" propio`
        : existed
        ? `${vehicle?.licensePlate || plate} → ${client.name}`
        : `${etiqueta}: cliente "Sin datos" creado — nombre y teléfono se piden al cobrar`, { duration: 5000 });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo resolver la patente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={onConsumidorFinal}
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        + Consumidor final (sin registrar)
      </button>
      <button
        type="button"
        onClick={abrir}
        className="mt-1 ml-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        + Cliente sin datos (solo patente)
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cliente sin datos</DialogTitle>
            <DialogDescription>
              {sinPatente
                ? 'Cargá la marca y el modelo del auto. Se crea un cliente "Sin datos" y los datos se piden al cobrar.'
                : 'Ingresá la patente del vehículo. Si el auto ya está cargado se autocompleta; si no, se crea un cliente "Sin datos" y los datos se piden al cobrar.'}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // El diálogo se renderiza en un portal, pero el evento igual sube
              // por el árbol de React hasta el <form> de la pantalla (turno,
              // remito, presupuesto) y le disparaba la validación completa:
              // aparecían "Cliente es requerido" y "Fecha es requerida" sin
              // haber tocado nada.
              e.stopPropagation();
              resolver();
            }}
            className="space-y-3"
          >
            {!sinPatente ? (
              <div className="flex items-center gap-2">
                <PlateScanButton onPlate={(p) => setPlateInput(p)} />
                <Input
                  autoFocus
                  value={plateInput}
                  onChange={(e) => setPlateInput(e.target.value)}
                  placeholder="AAA123 o AB123CD"
                  className="flex-1"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  autoFocus
                  value={marca}
                  onChange={(e) => setMarca(e.target.value)}
                  placeholder="Marca (ej: Ford)"
                />
                <Input
                  value={modelo}
                  onChange={(e) => setModelo(e.target.value)}
                  placeholder="Modelo (ej: Fiesta)"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setSinPatente((v) => !v)}
              className="text-xs font-medium text-primary hover:underline"
            >
              {sinPatente ? '← Tiene patente: buscarla' : 'El auto no tiene patente'}
            </button>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Guardando…' : sinPatente ? 'Crear sin patente' : 'Buscar / crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
